// pages/staffs/StaffDashboard.js -- Staff's "My IT Support Dashboard" (/staff/home).
//
// This is a DASHBOARD, deliberately distinct from Staff -> My Complaints
// (ComplaintsListPage.jsx), which stays the full list/management surface
// (search, status filter, full paginated table, detailed Status/TAT). Home
// shows only a summary: KPI counts, the latest 3 complaints as compact
// cards (never a full-width data table with column headers), a Quick
// Actions shortcut card, and an optional Pending Feedback widget. "View All
// Complaints" is the deliberate handoff to the real list page.
//
// Reuses the SAME pieces Staff -> My Complaints already uses -- no second
// complaint form, no second chat implementation, no second status/TAT rule
// set, no duplicate backend endpoint:
//   - Raise Complaint: the SAME <TaskCreateModal mode="complaint"> every
//     other "Raise Complaint" entry point uses (Category/Subcategory,
//     technician auto-assignment, the Complaint-Date lock, the inline
//     attachment step and rich success confirmation all come along for
//     free -- this file adds no complaint-creation logic of its own).
//   - Chat: the SAME <TaskChatPanel> (participant-authorized /tasks/:id/chat),
//     not a custom drawer over /tasks/:id/remarks.
//   - Data + scoping: the SAME GET /tasks/user/:id?complaintsOnly=1 call
//     ComplaintsListPage makes for Staff (self-scoped, IDOR-hardened
//     server-side by controllers/taskController.js#requireSelfOrDeny --
//     the URL's :id must equal the caller's own req.user.id) -- never all
//     of the company's complaints, never another user's. Latest-3 slicing
//     happens client-side over this same fetch -- no separate "recent"
//     endpoint.
//   - Status/TAT rendering + stat-count semantics: the SAME pure helpers
//     ComplaintsListPage.jsx imports (tatUtils.js#getTatInfo,
//     StatusChips.jsx#statusPillClass) and the SAME bucket rule (a
//     "Completed" count includes both completed and closed; "Unread Chats"
//     counts ROWS with at least one unread message, not the sum of unread
//     counts) -- copied here as a small local summarize() rather than
//     importing a non-exported function from another page, matching how
//     this codebase already keeps small per-page presentational helpers
//     local rather than centralizing them, while sharing the actual
//     pure-logic modules (tatUtils/StatusChips) that must never drift
//     between pages.
//   - Visual system: the SAME ".tlp-*" classes (PortalHomeCards.css) every
//     other portal "home" surface (Developer/Data-Analyst Dashboard) and
//     ComplaintsListPage itself already use -- this file adds only a small
//     amount of its own scoped CSS (".shd-*") for the compact complaint
//     card and the 2-column dashboard grid, neither of which exists
//     elsewhere yet.
//   - Review Forms: a compact, read-only widget over the SAME
//     GET /my-form-assignments endpoint RoleReviewFormsPage/useReviewForms.js
//     already use -- no new assignment-creation logic.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { FaPlus, FaSyncAlt, FaTicketAlt, FaClock, FaSpinner, FaCheckCircle, FaCommentDots, FaEye, FaHeadset, FaClipboardList, FaExclamationTriangle, FaList } from "react-icons/fa";
import api from "../../api";
import { AuthContext } from "../../context/AuthContext";
import TaskCreateModal from "../tasks/TaskCreateModal";
import TaskChatPanel from "../tasks/TaskChatPanel";
import { getTatInfo } from "../tasks/tatUtils";
import { statusPillClass } from "../tasks/StatusChips";
import { taskDisplayTitle } from "../../utils/taskDisplay";
import "../tasks/PortalHomeCards.css";

function normalizeRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

const formatDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return v; }
};

// Mirrors StatusChips.jsx's own local titleCase exactly -- not exported
// there either, so duplicated here rather than reaching into another
// page's private helper.
const titleCase = (v) => String(v || "-").replace(/_/g, " ").replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

// Mirrors ComplaintsListPage.jsx's own summarize() exactly (same bucket
// rule: "completed" count includes both completed and closed; "unread"
// counts rows with at least one unread message, not a sum of counts) --
// see this file's own header comment for why this is a local copy rather
// than an import.
function summarize(rows) {
  const counts = { total: rows.length, pending: 0, in_progress: 0, completed: 0, unread: 0 };
  rows.forEach((row) => {
    const status = String(row.status || "").toLowerCase();
    if (status === "pending") counts.pending += 1;
    else if (status === "in_progress") counts.in_progress += 1;
    else if (status === "completed" || status === "closed") counts.completed += 1;
    if (Number(row.unread_count) > 0) counts.unread += 1;
  });
  return counts;
}

// Compact card -- deliberately NOT a table row. No column headers, no
// full-width grid; just the handful of facts a dashboard summary needs
// (item 6 of the redesign spec). Same View/Chat actions as the full
// Complaints table, just smaller.
function RecentComplaintCard({ row, onView, onChat }) {
  const tat = getTatInfo(row);
  return (
    <div className="shd-complaint-card">
      <div className="shd-complaint-row1">
        <span className="shd-complaint-id">#{row.id}</span>
        <span className={statusPillClass(row.status)}>{titleCase(row.status)}</span>
      </div>
      <div className="shd-complaint-title">{taskDisplayTitle(row)}</div>
      <div className="shd-complaint-meta">
        {row.category_name || "—"}{row.sub_category_name ? ` / ${row.sub_category_name}` : ""}
      </div>
      <div className="shd-complaint-meta">Assigned: {row.assigned_to_name || "Pending assignment"}</div>
      <div className="shd-complaint-footer">
        <span className={`tlp-tat tlp-tat-${tat.tone}`}>{tat.label}</span>
        <span className="shd-complaint-date">{formatDate(row.created_at)}</span>
      </div>
      <div className="shd-complaint-actions">
        <Button size="sm" variant="outline-primary" onClick={onView}>View</Button>
        <Button size="sm" variant="outline-secondary" onClick={onChat}>
          Chat{Number(row.unread_count) > 0 && <span className="tlp-unread-badge" style={{ marginLeft: 5 }}>{row.unread_count}</span>}
        </Button>
      </div>
    </div>
  );
}

const RECENT_LIMIT = 3;

export default function StaffDashboard() {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const rolePrefix = user?.role || "staff";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showRaiseComplaint, setShowRaiseComplaint] = useState(false);
  const [chatTaskId, setChatTaskId] = useState(null);

  const [reviewTotal, setReviewTotal] = useState(0);

  // Same endpoint + same params ComplaintsListPage.jsx uses for Staff
  // (isStaffComplaints): GET /tasks/user/:id?complaintsOnly=1, scoped to
  // the logged-in user's OWN id only -- never a hardcoded or URL-derived
  // id. requireSelfOrDeny (backend) independently re-checks this too.
  const fetchComplaints = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/tasks/user/${user.id}`, { params: { complaintsOnly: 1 } });
      setRows(normalizeRows(res.data));
    } catch (err) {
      console.error("Fetch staff complaints error:", err);
      setError("Failed to load your complaints.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Read-only -- same GET /my-form-assignments RoleReviewFormsPage/
  // useReviewForms.js already use, filtered to this user's own pending
  // assignments. Never creates or modifies an assignment. Home only ever
  // needs the COUNT for its compact widget (item 8) -- the full list
  // belongs on Review Forms itself.
  const fetchReviewForms = async () => {
    if (!user?.id) return;
    try {
      const res = await api.get("/my-form-assignments", { params: { status: "pending", pageSize: 5 } });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setReviewTotal(Number(res.data?.meta?.total) || list.length);
    } catch (err) {
      console.error("Fetch review forms error:", err);
      setReviewTotal(0);
    }
  };

  useEffect(() => {
    fetchComplaints();
    fetchReviewForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const summary = useMemo(() => summarize(rows), [rows]);

  const recentRows = useMemo(
    () => [...rows].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, RECENT_LIMIT),
    [rows]
  );

  const openRaiseComplaint = () => setShowRaiseComplaint(true);

  return (
    <div className="tlp-page shd-page">
      <div className="tlp-header">
        <div>
          <div className="tlp-eyebrow">IT Support</div>
          <h1 className="tlp-title">My IT Support Dashboard</h1>
          <p className="tlp-subtitle">Track your complaints, requests and IT support updates.</p>
        </div>
        <div className="tlp-hactions">
          <Button className="tlp-btn tlp-btn-ghost" onClick={fetchComplaints} disabled={loading} title="Refresh">
            <FaSyncAlt style={{ fontSize: 12 }} />
          </Button>
          <Button className="tlp-btn tlp-btn-primary" onClick={openRaiseComplaint}>
            <FaPlus style={{ fontSize: 11 }} /> Raise Complaint
          </Button>
        </div>
      </div>

      {/* Quick Support banner -- same .tlp-hero pattern used elsewhere in
          the portal (e.g. DataAnalystDashboard.jsx), Jaipuria maroon/gold. */}
      <div className="tlp-hero">
        <div className="tlp-hero-main">
          <div className="tlp-hero-avatar"><FaHeadset /></div>
          <div>
            <div className="tlp-hero-greeting">Need IT Assistance?</div>
            <h2 className="tlp-hero-title">Facing an issue with your laptop, email, printer, network or other IT services?</h2>
            <p className="tlp-hero-subtitle">Raise a complaint and it will be routed to the right technician automatically.</p>
          </div>
        </div>
        <div className="tlp-hero-meta">
          <Button className="tlp-btn tlp-btn-primary" onClick={openRaiseComplaint}>
            <FaPlus style={{ fontSize: 11 }} /> Raise Complaint
          </Button>
        </div>
      </div>

      <div className="tlp-kpi-strip">
        <div className="tlp-kpi">
          <div className="tlp-kpi-icon tlp-kpi-icon-blue"><FaTicketAlt /></div>
          <div><span className="tlp-kpi-label">Total Complaints</span><strong className="tlp-kpi-val">{loading ? "…" : summary.total}</strong></div>
        </div>
        <div className="tlp-kpi">
          <div className="tlp-kpi-icon tlp-kpi-icon-amber"><FaClock /></div>
          <div><span className="tlp-kpi-label">Pending</span><strong className="tlp-kpi-val">{loading ? "…" : summary.pending}</strong></div>
        </div>
        <div className="tlp-kpi">
          <div className="tlp-kpi-icon tlp-kpi-icon-info"><FaSpinner /></div>
          <div><span className="tlp-kpi-label">In Progress</span><strong className="tlp-kpi-val">{loading ? "…" : summary.in_progress}</strong></div>
        </div>
        <div className="tlp-kpi">
          <div className="tlp-kpi-icon tlp-kpi-icon-green"><FaCheckCircle /></div>
          <div><span className="tlp-kpi-label">Completed</span><strong className="tlp-kpi-val">{loading ? "…" : summary.completed}</strong></div>
        </div>
        <div className="tlp-kpi">
          <div className="tlp-kpi-icon tlp-kpi-icon-purple"><FaCommentDots /></div>
          <div><span className="tlp-kpi-label">Unread Chats</span><strong className="tlp-kpi-val">{loading ? "…" : summary.unread}</strong></div>
        </div>
      </div>

      {/* Dashboard body: Recent Complaints (2/3) + Quick Actions/Review
          Forms (1/3) -- deliberately NOT a full-width table, so this page
          reads as a summary, not a second Complaints list. */}
      <div className="shd-dashboard-grid">
        <div className="tlp-card">
          <div className="tlp-card-head">
            <strong>Recent Complaints{rows.length > 0 && <span className="tlp-count"> ({rows.length})</span>}</strong>
          </div>

          <div className="shd-complaint-list">
            {loading ? (
              <div className="tlp-empty">
                <Spinner animation="border" />
                <p>Loading your complaints...</p>
              </div>
            ) : error ? (
              <div className="tlp-empty">
                <div className="tlp-empty-icon"><FaExclamationTriangle /></div>
                <p>{error}</p>
              </div>
            ) : rows.length === 0 ? (
              <div className="tlp-empty">
                <div className="tlp-empty-icon"><FaTicketAlt /></div>
                <h5>No complaints raised yet.</h5>
                <p>Need help with an IT issue?</p>
                <Button className="tlp-btn tlp-btn-primary" onClick={openRaiseComplaint}>
                  <FaPlus style={{ fontSize: 11 }} /> Raise Your First Complaint
                </Button>
              </div>
            ) : (
              recentRows.map((row) => (
                <RecentComplaintCard
                  key={row.id}
                  row={row}
                  onView={() => navigate(`/${rolePrefix}/tasks/${row.id}`)}
                  onChat={() => setChatTaskId(row.id)}
                />
              ))
            )}
          </div>

          {rows.length > 0 && (
            <div className="shd-view-all-footer">
              <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => navigate(`/${rolePrefix}/complaints`)}>
                View All Complaints
              </Button>
            </div>
          )}
        </div>

        <div className="shd-side-col">
          <div className="tlp-card">
            <div className="tlp-card-head"><strong>Quick Actions</strong></div>
            <div>
              <div className="tlp-formrow" role="button" tabIndex={0} onClick={openRaiseComplaint}>
                <div className="tlp-formrow-main">
                  <div className="tlp-formrow-icon"><FaPlus /></div>
                  <div className="tlp-formrow-text"><span className="tlp-formrow-title">Raise Complaint</span></div>
                </div>
                <span className="tlp-formrow-arrow">›</span>
              </div>
              <div className="tlp-formrow" role="button" tabIndex={0} onClick={() => navigate(`/${rolePrefix}/complaints`)}>
                <div className="tlp-formrow-main">
                  <div className="tlp-formrow-icon"><FaList /></div>
                  <div className="tlp-formrow-text"><span className="tlp-formrow-title">View All Complaints</span></div>
                </div>
                <span className="tlp-formrow-arrow">›</span>
              </div>
              <div className="tlp-formrow" role="button" tabIndex={0} onClick={() => navigate(`/${rolePrefix}/my-assigned-forms`)}>
                <div className="tlp-formrow-main">
                  <div className="tlp-formrow-icon"><FaClipboardList /></div>
                  <div className="tlp-formrow-text"><span className="tlp-formrow-title">Review Forms</span></div>
                </div>
                <span className="tlp-formrow-arrow">›</span>
              </div>
            </div>
          </div>

          {/* Optional -- only shown when the Staff user actually has
              pending review/feedback forms (item 8). */}
          {reviewTotal > 0 && (
            <div className="tlp-card">
              <div className="tlp-card-head"><strong>Pending Feedback</strong></div>
              <div className="shd-review-widget">
                <p className="shd-review-count">{reviewTotal} form{reviewTotal > 1 ? "s" : ""} waiting</p>
                <Button className="tlp-btn tlp-btn-primary tlp-btn-sm" onClick={() => navigate(`/${rolePrefix}/my-assigned-forms`)}>
                  Review Now
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <TaskCreateModal
        show={showRaiseComplaint}
        mode="complaint"
        onClose={() => setShowRaiseComplaint(false)}
        onCreated={fetchComplaints}
      />

      <TaskChatPanel
        taskId={chatTaskId}
        show={!!chatTaskId}
        onClose={() => setChatTaskId(null)}
        currentUserId={user?.id}
      />

      <style>{`
        .shd-dashboard-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 14px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .shd-dashboard-grid { grid-template-columns: 1fr; }
        }

        .shd-side-col {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .shd-complaint-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 14px 18px;
        }

        .shd-complaint-card {
          background: #fff;
          border: 1px solid rgba(20, 18, 14, 0.1);
          border-left: 3px solid var(--primary, #6d1530);
          border-radius: 12px;
          padding: 12px 14px;
        }

        .shd-complaint-row1 {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 4px;
        }

        .shd-complaint-id {
          font-size: 11px;
          font-weight: 800;
          color: #94a3b8;
        }

        .shd-complaint-title {
          font-size: 13.5px;
          font-weight: 700;
          color: #14151f;
          margin-bottom: 4px;
        }

        .shd-complaint-meta {
          font-size: 11.5px;
          color: #64748b;
          margin-bottom: 2px;
        }

        .shd-complaint-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 6px;
        }

        .shd-complaint-date {
          font-size: 10.5px;
          color: #94a3b8;
        }

        .shd-complaint-actions {
          display: flex;
          gap: 6px;
          margin-top: 8px;
        }

        .shd-view-all-footer {
          padding: 12px 18px;
          border-top: 1px solid #f1f5f9;
          text-align: center;
        }

        .shd-review-widget {
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
        }

        .shd-review-count {
          margin: 0;
          font-size: 13px;
          font-weight: 700;
          color: #14151f;
        }
      `}</style>
    </div>
  );
}
